import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'
import { useAuth } from '../../../../context/AuthContext'

export default function AdminProfileForm() {
  const { user } = useAuth()
  const [formData, setFormData] = useState({ full_name: '', phone: '', region: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [photoUrl, setPhotoUrl] = useState(null)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  useEffect(() => {
    if (!user) return
    supabase
      .from('supporters')
      .select('full_name, phone, region, profile_photo')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setFormData({ full_name: data.full_name || '', phone: data.phone || '', region: data.region || '' })
          setPhotoUrl(data.profile_photo || null)
        }
        setLoading(false)
      })
  }, [user])

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return }
    setUploading(true); setError(null)
    const fileExt = file.name.split('.').pop()
    const fileName = `${user.id}-photo.${fileExt}`
    const { error: uploadError } = await supabase.storage.from('profile-photos').upload(fileName, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('profile-photos').getPublicUrl(fileName)
    await supabase.from('supporters').update({ profile_photo: publicUrl }).eq('id', user.id)
    setPhotoUrl(publicUrl)
    setUploading(false)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true); setError(null); setSuccess(false)
    const { error } = await supabase.from('supporters').update(formData).eq('id', user.id)
    if (error) setError(error.message)
    else setSuccess(true)
    setSaving(false)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setPasswordError(null); setPasswordSuccess(false)
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return }
    if (newPassword.length < 8) { setPasswordError('Password must be at least 8 characters'); return }
    setChangingPassword(true)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) setPasswordError(error.message)
    else { setPasswordSuccess(true); setNewPassword(''); setConfirmPassword('') }
    setChangingPassword(false)
  }

  const initials = formData.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()
  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  if (loading) return null

  return (
    <div className="flex flex-col gap-6">

      {/* ── Personal Info ── */}
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Personal Information</div>

        {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>}
        {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">Profile updated successfully.</div>}

        {/* Photo */}
        <div className="flex items-center gap-5 mb-8 pb-8 border-b border-gray-200 dark:border-white/10">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-green-900/50 border-2 border-green-700/50 flex items-center justify-center shrink-0">
            {photoUrl
              ? <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
              : <span className="text-white font-black text-xl">{initials}</span>
            }
          </div>
          <div className="flex flex-col gap-2">
            <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors w-fit">
              {uploading ? 'Uploading...' : photoUrl ? 'Change Photo' : 'Upload Photo'}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} className="hidden" />
            </label>
            <p className="text-gray-400 text-xs">PNG or JPG — max 2MB</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex flex-col gap-4 max-w-lg">
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Full Name</label>
            <input type="text" value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })} className={inputClass} />
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Phone</label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+260 97 000 0000" className={inputClass} />
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Region / City</label>
            <input type="text" value={formData.region} onChange={e => setFormData({ ...formData, region: e.target.value })} placeholder="Mufulira" className={inputClass} />
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Email</label>
            <div className="px-4 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/5 text-gray-500 text-sm">{user?.email}</div>
          </div>
          <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors w-fit px-8 mt-2">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* ── Change Password ── */}
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Change Password</div>

        {passwordError && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{passwordError}</div>}
        {passwordSuccess && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">Password updated successfully.</div>}

        <form onSubmit={handleChangePassword} className="flex flex-col gap-4 max-w-lg">
          <div className="flex flex-col gap-2">
            <label className={labelClass}>New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" required minLength={8} className={inputClass} />
          </div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required className={inputClass} />
          </div>
          <button type="submit" disabled={changingPassword} className="bg-gray-900 dark:bg-white/10 hover:bg-gray-800 dark:hover:bg-white/20 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors w-fit px-8 mt-2">
            {changingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

    </div>
  )
}